import {
    ClockCircleOutlined,
    EuroOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import { Card as AntdCard, Button, Col, Divider, Drawer, message, Progress, Row, Skeleton, Space, Typography } from "antd";
import * as ROOMS_SERVICES from "api/services/rooms/rooms.services";
import { StudentAcceptClassSession, StudentJoinClass, StudentJoinClassSession, StudentJoinCollectiveClass } from "api/services/studentJoinClass/studentJoinClass.services";
import { StatusCode } from "common/enums";
import { AppNotification } from "components/appNotification";
import AppResult from "components/appResults";
import Breadcrumbs from "components/breadcrumbs";
import Card from "components/card";
import HelpPopover from "components/help";
import Flex from "components/libs/flex";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AppScreen from "screens/teacher/ScreenWithSidebar";
import ArrowBack from "../../../assets/images/app/arrow_back.svg";
import InvitationIcon from "../../../assets/images/app/invitation.svg";
import walletIcon from "../../../assets/images/app/wallet.png";
import s from "./Invitations.module.css";

const { Title, Text } = Typography;

const InvitationsList = () => {
    const { user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showAcceptInvitation, setShowAcceptInvitation] = useState<boolean>(false);
    const [isAcceptingInvitations, setIsAcceptingInvitations] = useState<boolean>(false);
    const [invitations, setInvitations] = useState<any>([]);
    const [invitation, setInvitation] = useState<any>();

    // FIX: Only one breakpoint state needed — the rest handled by CSS
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

    // FIX: Resize listener in useEffect with cleanup — no memory leak
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 767);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [couponCode, setCouponCode] = useState("");
    const coursePrice = 42;
    const walletBalance = 120.0;

    const fetchInvitations = async () => {
        try {
            setIsLoading(true);
            const response = await ROOMS_SERVICES.fetchInvitations(user?._id);
            if (response?.statusCode === StatusCode.OK) {
                const data = response?.data;
                const classInvitations = data?.classInvitations?.map((item: any) => ({ ...item, type: "classeInvitation" }));
                const sessionInvitations = data?.sessionInvitations?.map((item: any) => ({ ...item, type: "sessionInvitation" }));
                setInvitations([...classInvitations, ...sessionInvitations]);
            }
        } catch (error: any) {
            message.error(error?.message || "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInvitation = (invitation: any) => setInvitation(invitation);

    const handleCloseInvitationDrawer = () => {
        setInvitation(null);
        setShowAcceptInvitation(false);
    };

    const handleAcceptInvitations = () => {
        if (invitation?.type === "sessionInvitation") joinSession(invitation?.invitationToken);
        if (invitation?.type === "classeInvitation") joinCollectiveClass(invitation?.classe?.accessCode, invitation?.studentInvitationToken);
    };

    const joinClass = async (code: string) => {
        try {
            setIsAcceptingInvitations(true);
            if (code && user?._id) {
                const response = await StudentJoinClass(code, user?._id);
                if (response.statusCode === StatusCode.CREATED) {
                    AppNotification("Tu as rejoint la classe avec succès !", "AlbertVeryHappy");
                    setShowAcceptInvitation(false);
                }
            }
        } catch (error: any) {
            if (error.response.status === 409) message.warning("Vous avez déjà rejoint cette classe !");
            else if (error.response.status === 404) AppNotification("Le code saisi n'est pas valide", "AlbertSad");
            else message.error("Une erreur inconnue est survenue");
        } finally {
            setIsAcceptingInvitations(false);
        }
    };

    const joinCollectiveClass = async (code: string, token: string) => {
        try {
            if (code && user?._id && token) {
                setIsAcceptingInvitations(true);
                const response = await StudentJoinCollectiveClass(code, user?._id, token);
                if (response.statusCode === StatusCode.OK) {
                    AppNotification("Tu as rejoint la classe avec succès !", "AlbertVeryHappy");
                    setShowAcceptInvitation(false);
                    fetchInvitations();
                }
            }
        } catch (error: any) {
            if (error.response.status === 409) message.warning("Vous avez déjà rejoint cette classe !");
            else if (error.response.status === 404) AppNotification("Le code saisi n'est pas valide", "AlbertSad");
            else message.error("Une erreur inconnue est survenue");
        } finally {
            setIsAcceptingInvitations(false);
        }
    };

    const joinClassSession = async (studentId: string, token: string, collectiveId: string) => {
        try {
            if (studentId && token && collectiveId) {
                setIsAcceptingInvitations(true);
                const response = await StudentJoinClassSession(studentId, token, collectiveId);
                if (response.statusCode === StatusCode.CREATED) {
                    AppNotification("Tu as rejoint le cours collectif avec succès !", "AlbertVeryHappy");
                    setShowAcceptInvitation(false);
                }
            }
        } catch (error: any) {
            if (error.response.status === 409) message.warning("Vous avez déjà rejoint cette classe !");
            else if (error.response.status === 404) AppNotification("Le code saisi n'est pas valide", "AlbertSad");
            else message.error("Une erreur inconnue est survenue");
        } finally {
            setIsAcceptingInvitations(false);
        }
    };

    const joinSession = async (token: string) => {
        try {
            if (token) {
                setIsAcceptingInvitations(true);
                const response = await StudentAcceptClassSession(token);
                if (response.statusCode === StatusCode.OK) {
                    AppNotification("Tu as rejoint le cours collectif avec succès !", "AlbertVeryHappy");
                    setShowAcceptInvitation(false);
                    fetchInvitations();
                }
            }
        } catch (error: any) {
            if (error.response.status === 409) message.warning("Vous avez déjà rejoint cette classe !");
            else if (error.response.status === 404) AppNotification("Le code saisi n'est pas valide", "AlbertSad");
            else message.error("Une erreur inconnue est survenue");
        } finally {
            setIsAcceptingInvitations(false);
        }
    };

    const formatDate = (date: any) => {
        const formatted = new Intl.DateTimeFormat("fr-FR", {
            weekday: "long", day: "2-digit", month: "long", year: "numeric",
        }).format(new Date(date));
        return formatted.split(" ").map((word, index) =>
            index === 0 || index === 2 ? word.charAt(0).toUpperCase() + word.slice(1) : word
        ).join(" ");
    };

    useEffect(() => {
        if (user) fetchInvitations();
    }, [user]);

    useEffect(() => {
        if (invitation) setShowAcceptInvitation(true);
    }, [invitation]);

    return (
        <React.Fragment>
            {!isMobile && <Breadcrumbs />}
            <AppScreen>
                {/* Page Header */}
                <div className={s.pageHeader}>
                    <Space align="center" size="middle">
                        <div className="icon-wrapper" style={{ background: "#fff2e0" }}>
                            <img src={InvitationIcon} height={20} />
                        </div>
                        <Title level={4} style={{ margin: 0 }}>Mes invitations</Title>
                        <HelpPopover id={"invitations"} />
                    </Space>
                </div>

                {/* Main container */}
                <div className={s.mainContainer}>
                    {/* Wallet summary card */}
                    {isLoading ? (
                        <Skeleton />
                    ) : (
                        <div className={s.walletCard}>
                            <span className={s.walletTitle}>Mon wallet</span>
                            <div className={s.walletBalance}>
                                <span>Solde disponible</span>
                                <span className={s.walletAmount}>{user?.coins}(€)</span>
                            </div>
                        </div>
                    )}

                    {/* Invitations list */}
                    {isLoading ? (
                        <Skeleton />
                    ) : invitations.length > 0 ? (
                        <Flex>
                            {invitations.map((invitation: any) => {
                                const classType = invitation?.classe?.classType;
                                const isStandard = classType === "STANDARD";
                                const isOpen = classType === "OPEN";
                                const isSessionInvitation = invitation?.type === "sessionInvitation";
                                const isClasseInvitation = invitation?.type === "classeInvitation";

                                const formattedDate = new Intl.DateTimeFormat("fr-FR", {
                                    day: "2-digit", month: "long", year: "numeric",
                                }).format(isSessionInvitation ? new Date(invitation?.startDate) : new Date());

                                const sessionDate = formattedDate.split(" ")[0] + " " + formattedDate.split(" ")[1].charAt(0).toUpperCase() + formattedDate.slice(4);

                                return invitation?.status !== "ACTIVE" && (
                                    <Card key={invitation?._id} className={s.card}>
                                        {/* Ribbon — background is data-driven, keep inline */}
                                        <div
                                            className={s.ribbon}
                                            style={{
                                                background: invitation?.status === "ACTIVE" ? "#60CD95" : invitation?.status === "PENDING" ? "#fbe3a7" : "",
                                            }}
                                        >
                                            <span style={{ color: invitation?.status === "ACTIVE" ? "#FFF" : invitation?.status === "PENDING" ? "#9A6111" : "#FFF" }}>
                                                {invitation?.status === "PENDING" && "En attente"}
                                                {invitation?.status === "ACTIVE" && "Acceptée"}
                                            </span>
                                        </div>

                                        <div className={s.cardContent}>
                                            <div className={s.cardTitleBlock}>
                                                {invitation?.classe?.title && <h3 className={s.classTitle}>{invitation?.classe?.title}</h3>}
                                                {isSessionInvitation && invitation?.teacher && <p className={s.teacherName}>{invitation?.teacher?.firstname + " " + invitation?.teacher?.lastname}</p>}
                                                {isClasseInvitation && invitation?.classe?.teacher && <p className={s.teacherName}>{invitation?.classe?.teacher?.firstname + " " + invitation?.classe?.teacher?.lastname}</p>}
                                            </div>

                                            <div className={s.infoRow}><span>Type:</span><span>{isSessionInvitation ? "Cours individuel" : "Rejoindre une classe"}</span></div>

                                            {isSessionInvitation && (
                                                <>
                                                    <div className={s.infoRow}><span>Date:</span><span>{sessionDate}</span></div>
                                                    <div className={s.infoRow}><span>Durée:</span><span>{invitation?.duration}h</span></div>
                                                </>
                                            )}

                                            {isClasseInvitation && !isStandard && !isOpen && (
                                                <div className={s.infoRow}><span>Cours restants:</span><span>{invitation?.hoursLeft}</span></div>
                                            )}

                                            <div className={s.cardDivider} />

                                            {!isStandard && !isOpen && (
                                                <div className={`${s.infoRow} ${s.tarifRow}`}>
                                                    <span>Tarif horaires</span>
                                                    <span className={s.tarifAmount}>{invitation?.classe?.teacherCreditsCost}€</span>
                                                </div>
                                            )}
                                            {!isStandard && isOpen && (
                                                <div className={`${s.infoRow} ${s.tarifRow}`}>
                                                    <span>Tarif</span>
                                                    <span className={s.tarifAmount}>{invitation?.price}€</span>
                                                </div>
                                            )}

                                            <div className={s.buttonContainer}>
                                                <Button className="classBtn classBtn-green" onClick={() => handleInvitation(invitation)}>
                                                    Voir les détails
                                                </Button>
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </Flex>
                    ) : (
                        <div className={s.emptyState}>
                            <AppResult status={"404"} title={"Aucune invitation trouvée"} />
                        </div>
                    )}
                </div>

                {/* Details Drawer */}
                <Drawer
                    className="drawer"
                    onClose={handleCloseInvitationDrawer}
                    visible={showAcceptInvitation}
                    width={isMobile ? "100%" : "60%"}
                    title={
                        <div className={s.drawerTitle}>Historique des cours</div>
                    }
                    headerStyle={{ border: "none", paddingTop: "50px", paddingLeft: isMobile ? "20px" : "40px", backgroundColor: "transparent" }}
                    closeIcon={<img src={ArrowBack} height={isMobile ? 10 : 18} />}
                    drawerStyle={{ backgroundColor: "#FFF", borderTopLeftRadius: isMobile ? "0px" : "50px", borderBottomLeftRadius: isMobile ? "0px" : "50px", boxShadow: "-3px 3px 34px #39393929" }}
                >
                    <div className={s.drawerInner}>
                        <Row gutter={[24, 24]}>
                            {/* LEFT COLUMN */}
                            <Col xs={24} md={14} lg={15}>
                                {/* Course header */}
                                <div className={s.drawerCourseHeader}>
                                    <Title level={4} className={s.drawerCourseTitle}>
                                        {invitation?.classe?.title}
                                    </Title>
                                    {invitation?.classe?.teacher && <Text className={s.drawerCourseTeacher}>
                                        {invitation?.classe?.teacher?.firstname + " " + invitation?.classe?.teacher?.lastname}
                                    </Text>}
                                </div>

                                {/* Session info card */}
                                {invitation?.type === "sessionInvitation" && (
                                    <AntdCard className={s.infoCardYellow} bodyStyle={{ padding: 20 }}>
                                        <div className={s.infoCardHeader}>
                                            <ClockCircleOutlined className={s.iconYellow} />
                                            <Text strong className={s.infoCardTitle}>Information du cours</Text>
                                        </div>
                                        <Row gutter={[16, 16]}>
                                            {[
                                                { label: "Date", value: formatDate(invitation?.startDate) },
                                                { label: "Date", value: formatDate(invitation?.startDate) },
                                                { label: "Durée", value: `${invitation?.duration}h` },
                                                { label: "Format", value: "En ligne" },
                                            ].map((item, i) => (
                                                <Col xs={12} key={i}>
                                                    <div className={s.infoCell}>
                                                        <Text className={s.infoCellLabel}>{item.label}</Text>
                                                        <Text strong className={s.infoCellValue}>{item.value}</Text>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </AntdCard>
                                )}

                                {/* Pack progression card */}
                                {invitation?.type === "classeInvitation" && invitation?.classe?.classType === "PACK_PREDEFINED" && (
                                    <AntdCard className={s.progressCard} bodyStyle={{ padding: 20 }}>
                                        <div className={s.infoCardHeader}>
                                            <ClockCircleOutlined className={s.iconYellow} />
                                            <div>
                                                <Text strong className={s.progressCardTitle}>Progression actuelle de la classe</Text>
                                                <Text className={s.progressCardSubtitle}>
                                                    {invitation?.hoursConsumed > 0
                                                        ? "Cette classe a déjà commencé, vous rejoignez en cours de route."
                                                        : "Cette classe va bientôt commencer, rejoignez la maintenant."}
                                                </Text>
                                            </div>
                                        </div>
                                        <Row gutter={12} style={{ marginBottom: 18 }}>
                                            {[
                                                { label: "Cours effectués:", value: invitation?.hoursConsumed },
                                                { label: "Cours restants", value: invitation?.hoursLeft },
                                            ].map((item, i) => (
                                                <Col span={12} key={i}>
                                                    <div className={s.progressCell}>
                                                        <Text className={s.progressCellLabel}>{item.label}</Text>
                                                        <Text strong className={s.progressCellValue}>{item.value}</Text>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                        <div>
                                            <div className={s.progressBarHeader}>
                                                <Text className={s.progressLabel}>Progression:</Text>
                                                <Text strong className={s.progressValue}>{invitation?.hoursConsumed}/{invitation?.hoursLeft}</Text>
                                            </div>
                                            <Progress
                                                percent={(invitation?.hoursConsumed / invitation?.hoursLeft) * 100}
                                                showInfo={false}
                                                strokeColor={{ from: "#C9A20D", to: "#E0BB15" }}
                                                trailColor="#E8E2B8"
                                                strokeWidth={12}
                                                style={{ borderRadius: 10 }}
                                            />
                                        </div>
                                    </AntdCard>
                                )}

                                {/* Pricing card — session */}
                                {invitation?.type === "sessionInvitation" && (
                                    <AntdCard className={s.pricingCard} bodyStyle={{ padding: 20 }}>
                                        <div className={s.pricingCardHeader}>
                                            <EuroOutlined className={s.iconBlue} />
                                            <div>
                                                <Text strong className={s.pricingCardTitle}>Tarif pour rejoindre</Text>
                                                <Text className={s.pricingCardSubtitle}>Vous ne payez que les cours restants</Text>
                                            </div>
                                        </div>
                                        <Divider style={{ margin: "16px 0" }} />
                                        {invitation?.price && <div className={s.pricingRow}>
                                            <Text className={s.pricingLabel}>Prix du cours</Text>
                                            <Text strong className={s.pricingAmount}>{invitation?.price}€</Text>
                                        </div>}
                                    </AntdCard>
                                )}

                                {/* Pricing card — classe */}
                                {invitation?.type === "classeInvitation" && (
                                    <AntdCard className={s.pricingCard} bodyStyle={{ padding: 20 }}>
                                        <div className={s.pricingCardHeader}>
                                            <EuroOutlined className={s.iconBlue} />
                                            <div>
                                                <Text strong className={s.pricingCardTitle}>Tarif pour rejoindre</Text>
                                                <Text className={s.pricingCardSubtitle}>Vous ne payez que les cours restants</Text>
                                            </div>
                                        </div>
                                        {invitation?.classe?.classType === "PACK_PREDEFINED" && (
                                            <div className={s.pricingRow} style={{ marginTop: 30, marginBottom: 8 }}>
                                                <Text className={s.pricingSubLabel}>{invitation?.hoursLeft} cours x {invitation?.classe?.teacherCreditsCost}€</Text>
                                                <Text strong className={s.pricingSubAmount}>{invitation?.hoursLeft * invitation?.classe?.teacherCreditsCost}€</Text>
                                            </div>
                                        )}
                                        <Divider style={{ margin: "0 0 14px 0", borderColor: "#D6E4FD" }} />
                                        <div className={s.pricingRow}>
                                            <Text strong className={s.pricingFinalLabel}>Prix final</Text>
                                            {invitation?.classe?.classType === "PACK_PREDEFINED" ? (
                                                <Text strong className={s.pricingFinalAmount}>
                                                    {(invitation?.hoursLeft * invitation?.classe?.teacherCreditsCost).toFixed(2)}€
                                                </Text>
                                            ) : (
                                                <Text strong className={s.pricingFinalAmount}>Gratuit</Text>
                                            )}
                                        </div>
                                    </AntdCard>
                                )}
                            </Col>

                            {/* RIGHT COLUMN */}
                            <Col xs={24} md={10} lg={9}>
                                {/* Wallet card */}
                                <AntdCard className={s.drawerWalletCard} bodyStyle={{ padding: 20 }}>
                                    <div className={s.drawerWalletHeader}>
                                        <img src={walletIcon} height={25} />
                                        <Text strong className={s.drawerWalletTitle}>Mon wallet</Text>
                                    </div>
                                    <Title level={2} className={s.drawerWalletBalance}>
                                        {invitation?.student?.coins.toFixed(2)}€
                                    </Title>
                                    <Text className={s.drawerWalletLabel}>Solde disponible</Text>

                                    {((invitation?.type === "classeInvitation" && invitation?.classe?.classType !== "OPEN") || invitation?.type === "sessionInvitation") && (
                                        <Divider style={{ margin: "16px 0" }} />
                                    )}

                                    {invitation?.type === "classeInvitation" && invitation?.classe?.classType !== "OPEN" && invitation?.classe?.classType !== "STANDARD" && (
                                        <div className={s.pricingRow}>
                                            <Text className={s.afterPaymentLabel}>Après paiement</Text>
                                            <Text strong className={s.afterPaymentAmount}>
                                                {(invitation?.student?.coins - (invitation?.hoursLeft * invitation?.classe?.teacherCreditsCost)).toFixed(2)}€
                                            </Text>
                                        </div>
                                    )}

                                    {invitation?.type === "sessionInvitation" && (
                                        <div className={s.pricingRow}>
                                            <Text className={s.afterPaymentLabel}>Après paiement</Text>
                                            <Text strong className={s.afterPaymentAmount}>
                                                {(invitation?.student?.coins - invitation?.price).toFixed(2)}€
                                            </Text>
                                        </div>
                                    )}
                                </AntdCard>

                                {/* Summary card */}
                                {invitation?.type === "sessionInvitation" && (
                                    <AntdCard className={s.summaryCard} bodyStyle={{ padding: 20 }}>
                                        <Title level={5} className={s.summaryTitle}>Récapitulatif</Title>
                                        {[
                                            { label: "Type:", value: "Cours individuel" },
                                            { label: "Classe:", value: invitation?.classe?.title },
                                            { label: "Professeur:", value: `${invitation?.teacher?.firstname} ${invitation?.teacher?.lastname}` },
                                            { label: "Durée:", value: `${invitation?.duration}h` },
                                        ].map((item, index) => (
                                            <div key={index} className={s.summaryRow}>
                                                <Text className={s.summaryLabel}>{item.label}</Text>
                                                <Text strong className={s.summaryValue}>{item.value}</Text>
                                            </div>
                                        ))}
                                    </AntdCard>
                                )}

                                {invitation?.type === "classeInvitation" && (
                                    <AntdCard className={s.summaryCard} bodyStyle={{ padding: 20 }}>
                                        <Title level={5} className={s.summaryTitle}>Détails de la classe</Title>
                                        {[
                                            invitation?.classe?.classType !== "OPEN" && invitation?.classe?.classType !== "STANDARD" ? { label: "Total heures:", value: `${invitation?.totalHours}h` } : {},
                                            invitation?.classe?.classType !== "OPEN" && invitation?.classe?.classType !== "STANDARD" ? { label: "Prix par heure:", value: `${invitation?.classe?.teacherCreditsCost}€` } : {},
                                            invitation?.classe?.teacher ? { label: "Professeur:", value: `${invitation?.classe?.teacher?.firstname} ${invitation?.classe?.teacher?.lastname}` } : {},
                                        ].map((item, index) => (
                                            <div key={index} className={s.summaryRow}>
                                                <Text className={s.summaryLabel}>{item.label}</Text>
                                                <Text strong className={s.summaryValue}>{item.value}</Text>
                                            </div>
                                        ))}
                                    </AntdCard>
                                )}

                                {/* Bon à savoir */}
                                {invitation?.type === "sessionInvitation" && (
                                    <AntdCard className={s.infoBoxCard} bodyStyle={{ padding: 20 }}>
                                        <div className={s.infoBoxHeader}>
                                            <InfoCircleOutlined className={s.iconInfo} />
                                            <Text strong className={s.infoBoxTitle}>Bon à savoir</Text>
                                        </div>
                                        {[
                                            "Cours en visioconférence",
                                            "Lien d'accès envoyé par email",
                                            "Annulation gratuite 24h avant",
                                            "Enregistrement disponible après",
                                        ].map((info, index) => (
                                            <Text key={index} className={s.infoBoxItem}>{info}</Text>
                                        ))}
                                    </AntdCard>
                                )}
                            </Col>
                        </Row>
                    </div>

                    {/* Footer buttons */}
                    <div className={s.footerButtons}>
                        <Button size="large" className={s.cancelBtn} onClick={() => setShowAcceptInvitation(false)}>
                            Refuser
                        </Button>
                        <Button type="primary" size="large" className={s.acceptBtn} disabled={isAcceptingInvitations} loading={isAcceptingInvitations} onClick={handleAcceptInvitations}>
                            {invitation?.type === "sessionInvitation"
                                ? "Accepter et payer"
                                : invitation?.type === "classeInvitation" && invitation?.classe?.classType !== "OPEN" && invitation?.classe?.classType !== "STANDARD"
                                    ? "Accepter et payer"
                                    : "Accepter"}
                        </Button>
                    </div>
                </Drawer>
            </AppScreen>
        </React.Fragment>
    );
};

export default InvitationsList;